import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, Clock, ImageIcon } from 'lucide-react'
import { publicWebsiteImageUrl, type PublicCategory, type PublicCourseImage, type PublicCourseSummary } from '@/api/public'
import { isColourDark } from '@/lib/brandTheme'
import { formatPriceLabel } from '@/lib/pricing'

type Brand = { tenantName: string; tenantLogoUrl: string | null; primaryColour: string | null; secondaryColour: string | null }

/** The customer's name and logo in their own colours, with room for a link on the right. */
export function BrandHeader({ brand, subtitle, right }: { brand: Brand; subtitle: string; right?: ReactNode }) {
  const hasPrimary = !!brand.primaryColour
  const dark = hasPrimary ? isColourDark(brand.primaryColour!) : false
  const text = hasPrimary ? (dark ? 'text-white' : 'text-slate-900') : 'text-slate-900'
  const sub = hasPrimary ? (dark ? 'text-white/70' : 'text-slate-700') : 'text-slate-500'

  return (
    <header className={hasPrimary ? '' : 'bg-white border-b border-slate-200'} style={hasPrimary ? { backgroundColor: brand.primaryColour! } : undefined}>
      <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4">
        {brand.tenantLogoUrl && <img src={brand.tenantLogoUrl} alt={brand.tenantName} className="h-10 w-auto object-contain" />}
        <div className="flex-1 min-w-0">
          <p className={`text-xl font-bold truncate ${text}`}>{brand.tenantName}</p>
          <p className={`text-sm ${sub}`}>{subtitle}</p>
        </div>
        {right && <div className={`text-sm font-medium whitespace-nowrap ${hasPrimary ? (dark ? 'text-white/80' : 'text-slate-700') : ''}`}>{right}</div>}
      </div>
    </header>
  )
}

/** A course picture cropped to its box around the point the customer chose, at sizes to suit the screen. */
export function CoursePicture({ image, className, sizes, eager }: { image: PublicCourseImage; className?: string; sizes: string; eager?: boolean }) {
  return (
    <img
      src={publicWebsiteImageUrl(image.id, 960)}
      srcSet={`${publicWebsiteImageUrl(image.id, 480)} 480w, ${publicWebsiteImageUrl(image.id, 960)} 960w, ${publicWebsiteImageUrl(image.id, 1600)} 1600w`}
      sizes={sizes}
      alt={image.altText}
      loading={eager ? 'eager' : 'lazy'}
      className={className}
      style={{ objectFit: 'cover', objectPosition: `${image.focusX}% ${image.focusY}%` }}
    />
  )
}

export function PicturePlaceholder({ className }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className ?? ''}`} aria-hidden="true">
      <ImageIcon size={32} />
    </div>
  )
}

export function PageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export function NotFound({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center">
        <p className="text-lg font-semibold text-slate-700">{title}</p>
        <p className="text-sm text-slate-400 mt-1">{hint}</p>
      </div>
    </div>
  )
}

/**
 * A link to another CertSlate page. On the hosted pages it is an ordinary in-app link. Inside a customer's iframe it
 * is a full address that opens in a new tab, so booking never happens inside someone else's page.
 */
export function AppLink({ to, embed, className, children }: { to: string; embed?: boolean; className?: string; children: ReactNode }) {
  if (!embed) return <Link to={to} className={className}>{children}</Link>
  return <a href={`${window.location.origin}${to}`} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
}

/**
 * One course on the catalogue. On the hosted page it opens the hosted course page. In an embed it opens the course's
 * own page on the customer's website if it has one (replacing the frame's page, so visitors stay on their site),
 * otherwise the hosted course page in a new tab.
 */
export function CourseCard({ tenantSlug, course, category, embed, fmtMoney, formatDate }: {
  tenantSlug: string
  course: PublicCourseSummary
  category?: PublicCategory
  embed?: boolean
  fmtMoney: (n: number) => string
  formatDate: (value: string, opts?: Intl.DateTimeFormatOptions) => string
}) {
  const price = formatPriceLabel(course.pricingModel, course.price, course.flatFeeAmount, course.flatFeeCapacity, fmtMoney)
  const className = 'group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all hover:border-[var(--brand-accent)] hover:shadow-sm'
  const body = (
    <>
      {course.image
        ? <CoursePicture image={course.image} sizes="(min-width: 1024px) 320px, (min-width: 640px) 45vw, 100vw" className="aspect-[3/2] w-full" />
        : <PicturePlaceholder className="aspect-[3/2] w-full" />}
      <div className="flex flex-1 flex-col gap-2 p-4">
        {category && <p className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-accent)]">{category.name}</p>}
        <h2 className="font-semibold text-slate-900 group-hover:text-[var(--brand-accent)]">{course.name}</h2>
        {course.summary && <p className="line-clamp-3 text-sm text-slate-600">{course.summary}</p>}
        <div className="mt-auto space-y-1 pt-2 text-sm text-slate-500">
          {course.duration && <p className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400" />{course.duration}</p>}
          <p className="flex items-center gap-1.5">
            <CalendarDays size={14} className="text-slate-400" />
            {course.nextSessionStartsAt
              ? `Next: ${formatDate(course.nextSessionStartsAt, { day: 'numeric', month: 'short', year: 'numeric' })}`
              : 'No dates yet'}
          </p>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="font-bold text-slate-900">{price ? (course.nextSessionStartsAt ? price : `From ${price}`) : ''}</span>
          <span className="text-sm font-medium text-[var(--brand-accent)]">View course →</span>
        </div>
      </div>
    </>
  )

  if (embed && course.websiteUrl) {
    return <a href={course.websiteUrl} target="_top" className={className}>{body}</a>
  }
  return <AppLink to={`/courses/${tenantSlug}/${course.slug}`} embed={embed} className={className}>{body}</AppLink>
}
